import { useState } from "react";
import { Card, CardHeader } from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import { Edit2, Trash2, Save, X } from "lucide-react";
import type Tag from "@/models/tag/tag";
import { PermissionGate } from "@/lib/permissions/PermissionGate";
import { Permission } from "@/lib/permissions/Permission.enum";
import { TagType } from "@/models/tag/tag";

interface TagCardProps {
  tag: Tag;
  onUpdate: (tagId: string, name: string, tagType: TagType) => Promise<boolean>;
  onDelete: (tagId: string) => Promise<boolean>;
}

export function TagCard({ tag, onUpdate, onDelete }: TagCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(tag.name);
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    if (editName.trim() === tag.name) {
      setIsEditing(false);
      return;
    }

    setIsLoading(true);
    const success = await onUpdate(tag.id, editName.trim(), tag.tagType);
    setIsLoading(false);

    if (success) {
      setIsEditing(false);
    } else {
      setEditName(tag.name); // Reset on failure
    }
  };

  const handleCancel = () => {
    setEditName(tag.name);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (window.confirm(`Er du sikker på, at du vil slette "${tag.name}"?`)) {
      setIsLoading(true);
      await onDelete(tag.id);
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          {isEditing ? (
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSave();
                } else if (e.key === "Escape") {
                  handleCancel();
                }
              }}
              disabled={isLoading}
              autoFocus
              className="mr-2"
            />
          ) : (
            <p className="font-medium text-sm">{tag.name}</p>
          )}
          <div className="flex gap-1 shrink-0">
            {!isEditing ? (
              <>
                <PermissionGate allOf={[Permission.UpdateTag]}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    disabled={isLoading}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                </PermissionGate>
                <PermissionGate allOf={[Permission.DeleteTag]}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDelete}
                    disabled={isLoading}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </PermissionGate>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSave}
                  disabled={isLoading || !editName.trim()}
                >
                  <Save className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
    </Card>
  );
}
